<template>
  <PageContainer v-if="isPropertyLoaded" class="property-dashboard-layout" :class="{ mobile: isMobile }">
    <View gap="xl" direction="column" :grow="1" class="main-content">
      <PropertyDashboardTitle />

      <View direction="column" gap="md">
        <PropertyDashboardStats :property-id="propertyId" />

        <View gap="md" direction="row" class="widgets">
          <PropertyDashboardAutoUpdate class="widget-item" />
          <PropertyDashboardListingSettings class="widget-item" />
        </View>

        <PropertyDashboardAccessInstructions />
      </View>

      <div ref="engagementSection" id="pd-engagement">
        <PropertyDashboardEngagement />
      </div>

      <PropertyDashboardUpcomingShowings />
    </View>

    <View direction="column" :shrink="0" class="aside">
      <PropertyDashboardActivityFeed :limit="8" />
      <PropertyDashboardConvoSummary :limit="5" />
    </View>
  </PageContainer>
</template>

<script setup lang="ts">
import { until } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { PageContainer, View } from '@tallo/design-system';
import { usePropertyStore } from '@tallo/investor/property';

import productTourService from '../../../product-tour/product-tour.service';
import PropertyDashboardAccessInstructions from './property-dashboard-access-instructions.vue';
import PropertyDashboardActivityFeed from './property-dashboard-activity-feed.vue';
import PropertyDashboardAutoUpdate from './property-dashboard-auto-update.vue';
import PropertyDashboardConvoSummary from './property-dashboard-convo-summary.vue';
import PropertyDashboardEngagement from './property-dashboard-engagement.vue';
import PropertyDashboardListingSettings from './property-dashboard-listing-settings.vue';
import PropertyDashboardStats from './property-dashboard-stats.vue';
import PropertyDashboardTitle from './property-dashboard-title.vue';
import PropertyDashboardUpcomingShowings from './property-dashboard-upcoming-showings.vue';

const route = useRoute();
const propertyId = computed(() => route.params.propertyId as string);
const propertyStore = usePropertyStore();

const isPropertyLoaded = computed(() => propertyStore.isLoaded);
const property = computed(() => propertyStore.property);
const engagementSection = ref<HTMLElement>();

const isMobile = ref(false);

function checkScreenSize() {
  isMobile.value = window.innerWidth < 1024;
}

onMounted(() => {
  checkScreenSize();
  window.addEventListener('resize', checkScreenSize);
  scrollToSection();
});

onUnmounted(() => {
  window.removeEventListener('resize', checkScreenSize);
  productTourService.stopActiveTour('dashboard');
});

watch(
  property,
  (newProperty) => {
    if (newProperty) {
      productTourService.tryStartDashboardTour(propertyId.value);
    }
  },
  { immediate: true },
);

async function scrollToSection() {
  await until(isPropertyLoaded).toMatch((isLoaded) => !!isLoaded);

  const section = getSectionToScroll();

  if (section) {
    setTimeout(() => section.scrollIntoView({ behavior: 'smooth' }), 100);
  }
}

function getSectionToScroll(): HTMLElement | undefined {
  switch (route.hash) {
    case '#escalations-section':
    case '#showing-requests-section':
      return engagementSection.value;
  }
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.property-dashboard-layout {
  // Figma: 36px gap between the main column and the aside (node 15639:11429).
  --gap: 2.25rem;
  // Figma: aside is 394px wide (node 15639:11553).
  --aside-width: 24.625rem;

  display: flex;
  gap: var(--gap);

  .main-content {
    max-width: calc(100% - var(--aside-width) - var(--gap));
    width: calc(100% - var(--aside-width) - var(--gap));

    // One rhythm on a phone: the sections sit as close as the cards inside them, so the page header
    // no longer floats half a card's height above the card it heads.
    @include mobile {
      gap: var(--t-spacing-md);
    }
  }

  .aside {
    width: var(--aside-width);
    // Figma: 36px gap between the two side sections (activity feed / convo summary).
    gap: 2.25rem;
  }

  &.mobile {
    flex-direction: column;

    .main-content,
    .aside {
      width: 100%;
      max-width: 100%;
    }
  }
}

.widgets {
  @include mobile {
    flex-direction: column !important;
  }
}

.widget-item {
  flex: 1;
  min-width: 0;

  @include mobile {
    width: 100%;
  }
}
</style>
